import * as T from './offersReportConstants';
import type {
  OffersSummaryResponse,
  OfferKpiComparisonResponse,
  TopOffersResponse,
  OfferRedemptionTrendResponse,
  OfferDiscountBreakdownResponse,
  OfferDiscountByTypeResponse,
  OfferDetailResponse,
} from '../../../interface/offersReportInterface';

export interface TopOffersRequestParams {
  locationId: string;
  page: number;
  size: number;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  startDate?: string;
  endDate?: string;
  // Case-insensitive offer-name substring filter (ZAR-1471 ③) — `search` query param.
  search?: string;
  // Selected status enums (ACTIVE | PAUSED | ARCHIVED), comma-joined into the
  // `status` query param. Omitted when empty (= All).
  status?: string;
}

// ── Offers Summary ───────────────────────────────────────────────────────────

export const fetchOffersSummaryRequest = (locationId: string, startDate?: string, endDate?: string) => ({
  type: T.FETCH_OFFERS_SUMMARY_REQUEST as typeof T.FETCH_OFFERS_SUMMARY_REQUEST,
  payload: { locationId, startDate, endDate },
});

export const fetchOffersSummarySuccess = (data: OffersSummaryResponse) => ({
  type: T.FETCH_OFFERS_SUMMARY_SUCCESS as typeof T.FETCH_OFFERS_SUMMARY_SUCCESS,
  payload: data,
});

export const fetchOffersSummaryFailure = (error: string) => ({
  type: T.FETCH_OFFERS_SUMMARY_FAILURE as typeof T.FETCH_OFFERS_SUMMARY_FAILURE,
  payload: { error },
});

// ── KPI Comparison ───────────────────────────────────────────────────────────

export const fetchOfferKpiComparisonRequest = (locationId: string, startDate?: string, endDate?: string) => ({
  type: T.FETCH_OFFER_KPI_COMPARISON_REQUEST as typeof T.FETCH_OFFER_KPI_COMPARISON_REQUEST,
  payload: { locationId, startDate, endDate },
});

export const fetchOfferKpiComparisonSuccess = (data: OfferKpiComparisonResponse) => ({
  type: T.FETCH_OFFER_KPI_COMPARISON_SUCCESS as typeof T.FETCH_OFFER_KPI_COMPARISON_SUCCESS,
  payload: data,
});

export const fetchOfferKpiComparisonFailure = (error: string) => ({
  type: T.FETCH_OFFER_KPI_COMPARISON_FAILURE as typeof T.FETCH_OFFER_KPI_COMPARISON_FAILURE,
  payload: { error },
});

// ── Top Offers ───────────────────────────────────────────────────────────────

export const fetchTopOffersRequest = (params: TopOffersRequestParams) => ({
  type: T.FETCH_TOP_OFFERS_REQUEST as typeof T.FETCH_TOP_OFFERS_REQUEST,
  payload: params,
});

export const fetchTopOffersSuccess = (data: TopOffersResponse) => ({
  type: T.FETCH_TOP_OFFERS_SUCCESS as typeof T.FETCH_TOP_OFFERS_SUCCESS,
  payload: data,
});

export const fetchTopOffersFailure = (error: string) => ({
  type: T.FETCH_TOP_OFFERS_FAILURE as typeof T.FETCH_TOP_OFFERS_FAILURE,
  payload: { error },
});

// ── Offers KPI row (Active Offers + Top Offer) ───────────────────────────────
// Hits the same top-offers endpoint as the table but takes ONLY location + date:
// no search, status, sort or page. That keeps the two KPI tiles pinned to the
// selected period regardless of how the user filters or sorts the table below.

export const fetchOffersKpiRequest = (locationId: string, startDate?: string, endDate?: string) => ({
  type: T.FETCH_OFFERS_KPI_REQUEST as typeof T.FETCH_OFFERS_KPI_REQUEST,
  payload: { locationId, startDate, endDate },
});

export const fetchOffersKpiSuccess = (data: TopOffersResponse) => ({
  type: T.FETCH_OFFERS_KPI_SUCCESS as typeof T.FETCH_OFFERS_KPI_SUCCESS,
  payload: data,
});

export const fetchOffersKpiFailure = (error: string) => ({
  type: T.FETCH_OFFERS_KPI_FAILURE as typeof T.FETCH_OFFERS_KPI_FAILURE,
  payload: { error },
});

// ── Redemption Trend ─────────────────────────────────────────────────────────

export const fetchOfferRedemptionTrendRequest = (locationId: string, startDate?: string, endDate?: string) => ({
  type: T.FETCH_OFFER_REDEMPTION_TREND_REQUEST as typeof T.FETCH_OFFER_REDEMPTION_TREND_REQUEST,
  payload: { locationId, startDate, endDate },
});

export const fetchOfferRedemptionTrendSuccess = (data: OfferRedemptionTrendResponse) => ({
  type: T.FETCH_OFFER_REDEMPTION_TREND_SUCCESS as typeof T.FETCH_OFFER_REDEMPTION_TREND_SUCCESS,
  payload: data,
});

export const fetchOfferRedemptionTrendFailure = (error: string) => ({
  type: T.FETCH_OFFER_REDEMPTION_TREND_FAILURE as typeof T.FETCH_OFFER_REDEMPTION_TREND_FAILURE,
  payload: { error },
});

// ── Discount Breakdown ───────────────────────────────────────────────────────

export const fetchOfferDiscountBreakdownRequest = (locationId: string, startDate?: string, endDate?: string) => ({
  type: T.FETCH_OFFER_DISCOUNT_BREAKDOWN_REQUEST as typeof T.FETCH_OFFER_DISCOUNT_BREAKDOWN_REQUEST,
  payload: { locationId, startDate, endDate },
});

export const fetchOfferDiscountBreakdownSuccess = (data: OfferDiscountBreakdownResponse) => ({
  type: T.FETCH_OFFER_DISCOUNT_BREAKDOWN_SUCCESS as typeof T.FETCH_OFFER_DISCOUNT_BREAKDOWN_SUCCESS,
  payload: data,
});

export const fetchOfferDiscountBreakdownFailure = (error: string) => ({
  type: T.FETCH_OFFER_DISCOUNT_BREAKDOWN_FAILURE as typeof T.FETCH_OFFER_DISCOUNT_BREAKDOWN_FAILURE,
  payload: { error },
});

// ── Discount by Type ─────────────────────────────────────────────────────────

export const fetchOfferDiscountByTypeRequest = (locationId: string, startDate?: string, endDate?: string) => ({
  type: T.FETCH_OFFER_DISCOUNT_BY_TYPE_REQUEST as typeof T.FETCH_OFFER_DISCOUNT_BY_TYPE_REQUEST,
  payload: { locationId, startDate, endDate },
});

export const fetchOfferDiscountByTypeSuccess = (data: OfferDiscountByTypeResponse) => ({
  type: T.FETCH_OFFER_DISCOUNT_BY_TYPE_SUCCESS as typeof T.FETCH_OFFER_DISCOUNT_BY_TYPE_SUCCESS,
  payload: data,
});

export const fetchOfferDiscountByTypeFailure = (error: string) => ({
  type: T.FETCH_OFFER_DISCOUNT_BY_TYPE_FAILURE as typeof T.FETCH_OFFER_DISCOUNT_BY_TYPE_FAILURE,
  payload: { error },
});

// ── Individual Offer Detail ──────────────────────────────────────────────────

export const fetchOfferDetailRequest = (params: { locationId: string; offerId: string; startDate?: string; endDate?: string }) => ({
  type: T.FETCH_OFFER_DETAIL_REQUEST as typeof T.FETCH_OFFER_DETAIL_REQUEST,
  payload: params,
});

export const fetchOfferDetailSuccess = (data: OfferDetailResponse) => ({
  type: T.FETCH_OFFER_DETAIL_SUCCESS as typeof T.FETCH_OFFER_DETAIL_SUCCESS,
  payload: data,
});

export const fetchOfferDetailFailure = (error: string) => ({
  type: T.FETCH_OFFER_DETAIL_FAILURE as typeof T.FETCH_OFFER_DETAIL_FAILURE,
  payload: { error },
});

export const clearOfferDetail = () => ({
  type: T.CLEAR_OFFER_DETAIL as typeof T.CLEAR_OFFER_DETAIL,
});

export type OffersReportAction =
  | ReturnType<typeof fetchOffersSummaryRequest>
  | ReturnType<typeof fetchOffersSummarySuccess>
  | ReturnType<typeof fetchOffersSummaryFailure>
  | ReturnType<typeof fetchOfferKpiComparisonRequest>
  | ReturnType<typeof fetchOfferKpiComparisonSuccess>
  | ReturnType<typeof fetchOfferKpiComparisonFailure>
  | ReturnType<typeof fetchTopOffersRequest>
  | ReturnType<typeof fetchTopOffersSuccess>
  | ReturnType<typeof fetchTopOffersFailure>
  | ReturnType<typeof fetchOffersKpiRequest>
  | ReturnType<typeof fetchOffersKpiSuccess>
  | ReturnType<typeof fetchOffersKpiFailure>
  | ReturnType<typeof fetchOfferRedemptionTrendRequest>
  | ReturnType<typeof fetchOfferRedemptionTrendSuccess>
  | ReturnType<typeof fetchOfferRedemptionTrendFailure>
  | ReturnType<typeof fetchOfferDiscountBreakdownRequest>
  | ReturnType<typeof fetchOfferDiscountBreakdownSuccess>
  | ReturnType<typeof fetchOfferDiscountBreakdownFailure>
  | ReturnType<typeof fetchOfferDiscountByTypeRequest>
  | ReturnType<typeof fetchOfferDiscountByTypeSuccess>
  | ReturnType<typeof fetchOfferDiscountByTypeFailure>
  | ReturnType<typeof fetchOfferDetailRequest>
  | ReturnType<typeof fetchOfferDetailSuccess>
  | ReturnType<typeof fetchOfferDetailFailure>
  | ReturnType<typeof clearOfferDetail>;
